import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { facilitatorColors, getFacilitatorColor } from '../../lib/facilitatorColors';
import { fetchSessionsForDateRange } from '../../lib/airtable';
import { getViewSOLink } from '../../lib/contextAwareRouting';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Clock,
  Users,
  ExternalLink,
  Filter
} from 'lucide-react';

// Session tooltip component for showing full session details on hover
const SessionTooltip = ({ session, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const facilitators = session.facilitators && session.facilitators.length > 0 
    ? session.facilitators 
    : [{ 
        id: session.facilitatorId, 
        name: session.facilitatorName || 'Unassigned', 
        color: session.facilitatorColor || 'gray' 
      }];

  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {isVisible && (
        <>
          {/* Backdrop for z-index */}
          <div className="fixed inset-0 pointer-events-none z-40" />
          
          {/* Tooltip */}
          <div 
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm pointer-events-none transform -translate-x-1/2 -translate-y-full"
            style={{
              left: position.x,
              top: position.y,
            }}
          >
            {/* Arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200"></div>
            
            {/* Content */}
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                {session.sessionName}
              </h4>
              
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-1 text-gray-600">
                  <Clock className="h-3 w-3" />
                  <span>{session.dateTime}</span>
                </div>
                
                <div className="flex items-center space-x-1 text-gray-600">
                  <Calendar className="h-3 w-3" />
                  <span>SO: {session.soNumber}</span>
                </div>
                
                <div className="flex items-center space-x-1 text-gray-600">
                  <Users className="h-3 w-3" />
                  <span>{session.expectedAttendees} expected attendees</span>
                </div>
                
                <div className="text-gray-600">
                  <strong>Program:</strong> {session.programName}
                </div>
                
                {session.clientName && (
                  <div className="text-gray-600">
                    <strong>Client:</strong> {session.clientName}
                  </div>
                )}
                
                <div className="text-gray-600">
                  <strong>Status:</strong> {session.status}
                </div>
                
                <div className="pt-1">
                  <div className="text-gray-600 mb-1">
                    <strong>Facilitator{facilitators.length > 1 ? 's' : ''}:</strong>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {facilitators.map((facilitator, index) => {
                      const colors = facilitatorColors[facilitator.color] || facilitatorColors.gray;
                      return (
                        <span 
                          key={facilitator.id || index}
                          className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
                        >
                          {facilitator.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};


const SessionsCalendar = ({ sessions: fallbackSessions = [] }) => {
  const location = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day'
  const [selectedFacilitators, setSelectedFacilitators] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  
  // Dynamic session loading state
  const [dynamicSessions, setDynamicSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  // Function to fetch sessions for a specific date range
  const fetchSessionsForView = useCallback(async (startDate, endDate) => {
    // Don't refetch if we already have data for this exact range
    if (dateRange.start && dateRange.end && 
        startDate.getTime() === dateRange.start.getTime() && 
        endDate.getTime() === dateRange.end.getTime()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessions = await fetchSessionsForDateRange(startDate, endDate);
      setDynamicSessions(sessions);
      setDateRange({ start: startDate, end: endDate });
    } catch (err) {
      console.error('Error fetching sessions for calendar:', err);
      setError('Failed to load sessions for this date range');
      // Fall back to empty array but keep trying
      setDynamicSessions([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Calculate the date range for the current view
  const getViewDateRange = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (view === 'month') {
      // Month view - show full month plus some padding for partial weeks
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Start from Monday of the week containing the first day
      const startDate = new Date(firstDay);
      const dayOfWeek = firstDay.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startDate.setDate(firstDay.getDate() + mondayOffset);
      
      // End on Friday of the week containing the last day  
      const endDate = new Date(lastDay);
      const lastDayOfWeek = lastDay.getDay();
      const fridayOffset = lastDayOfWeek === 0 ? -2 : 5 - lastDayOfWeek;
      endDate.setDate(lastDay.getDate() + fridayOffset);
      
      return { startDate, endDate };
    } else if (view === 'week') {
      // Week view - current week
      const startDate = new Date(currentDate);
      const dayOfWeek = startDate.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startDate.setDate(currentDate.getDate() + mondayOffset);
      
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 4); // Friday
      
      return { startDate, endDate };
    } else {
      // Day view - just the current day
      const startDate = new Date(currentDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(currentDate);
      endDate.setHours(23, 59, 59, 999);
      
      return { startDate, endDate };
    }
  }, [currentDate, view]);

  // Effect to load sessions when view or date changes
  useEffect(() => {
    const { startDate, endDate } = getViewDateRange();
    fetchSessionsForView(startDate, endDate);
  }, [currentDate, view, getViewDateRange, fetchSessionsForView]);

  // Use dynamic sessions if available, otherwise fall back to passed sessions
  const sessions = dynamicSessions.length > 0 ? dynamicSessions : fallbackSessions;

  // Generate calendar data based on current view
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (view === 'month') {
      // Month view logic (existing)
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const startDate = new Date(firstDay);
      const dayOfWeek = firstDay.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startDate.setDate(firstDay.getDate() + mondayOffset);
      
      const endDate = new Date(lastDay);
      const lastDayOfWeek = lastDay.getDay();
      const fridayOffset = lastDayOfWeek === 0 ? -2 : 5 - lastDayOfWeek;
      endDate.setDate(lastDay.getDate() + fridayOffset);
      
      const days = [];
      const current = new Date(startDate);
      
      while (current <= endDate) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      return {
        year,
        month,
        days,
        displayName: firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        view: 'month'
      };
    } else if (view === 'week') {
      // Week view logic
      const currentDay = currentDate.getDay();
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
      const startDate = new Date(currentDate);
      startDate.setDate(currentDate.getDate() + mondayOffset);
      
      const days = [];
      for (let i = 0; i < 5; i++) { // Monday to Friday
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + i);
        days.push(day);
      }
      
      const endDate = days[4]; // Friday
      const weekRange = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      
      return {
        year,
        month,
        days,
        displayName: `Week of ${weekRange}`,
        view: 'week'
      };
    } else {
      // Day view logic
      const days = [new Date(currentDate)];
      
      return {
        year,
        month,
        days,
        displayName: currentDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        }),
        view: 'day'
      };
    }
  }, [currentDate, view]);

  // Extract unique facilitators from sessions for filtering
  const availableFacilitators = useMemo(() => {
    const facilitatorSet = new Set();
    sessions.forEach(session => {
      // Handle multiple facilitators per session
      if (session.facilitators && session.facilitators.length > 0) {
        session.facilitators.forEach(facilitator => {
          const facilitatorKey = `${facilitator.id || 'unassigned'}:${facilitator.name || 'Unassigned'}:${facilitator.color || 'gray'}`;
          facilitatorSet.add(facilitatorKey);
        });
      } else {
        // Fallback to single facilitator for backward compatibility
        const facilitatorKey = `${session.facilitatorId || 'unassigned'}:${session.facilitatorName || 'Unassigned'}:${session.facilitatorColor || 'gray'}`;
        facilitatorSet.add(facilitatorKey);
      }
    });
    
    return Array.from(facilitatorSet).map(facilitatorKey => {
      const [id, name, color] = facilitatorKey.split(':');
      return {
        id: id === 'unassigned' ? null : id,
        name: name,
        color: color,
        ...facilitatorColors[color] || facilitatorColors.gray
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [sessions]);

  // Filter sessions based on selected facilitators
  const filteredSessions = useMemo(() => {
    if (selectedFacilitators.size === 0) {
      return sessions; // Show all if no filters selected
    }
    
    return sessions.filter(session => {
      // Check if any of the session's facilitators match the selected filters
      if (session.facilitators && session.facilitators.length > 0) {
        return session.facilitators.some(facilitator => 
          selectedFacilitators.has(facilitator.id || 'unassigned')
        );
      } else {
        // Fallback to single facilitator for backward compatibility
        const facilitatorId = session.facilitatorId || 'unassigned';
        return selectedFacilitators.has(facilitatorId);
      }
    });
  }, [sessions, selectedFacilitators]);

  // Group sessions by date and sort by time
  const sessionsByDate = useMemo(() => {
    const grouped = {};
    
    filteredSessions.forEach(session => {
      if (!session.rawStart) return;
      
      const sessionDate = new Date(session.rawStart);
      const dateKey = `${sessionDate.getFullYear()}-${sessionDate.getMonth()}-${sessionDate.getDate()}`;
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(session);
    });
    
    // Sort sessions within each date by time (earliest first)
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        const timeA = new Date(a.rawStart).getTime();
        const timeB = new Date(b.rawStart).getTime();
        return timeA - timeB;
      });
    });
    
    return grouped;
  }, [filteredSessions]);

  // Get sessions for a specific date
  const getSessionsForDate = (date) => {
    const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return sessionsByDate[dateKey] || [];
  };

  // Navigation functions
  const goToPrevious = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else { // day
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };

  const goToNext = () => {
    if (view === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (view === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else { // day
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Filter management functions
  const toggleFacilitatorFilter = (facilitatorId) => {
    const newSelected = new Set(selectedFacilitators);
    if (newSelected.has(facilitatorId)) {
      newSelected.delete(facilitatorId);
    } else {
      newSelected.add(facilitatorId);
    }
    setSelectedFacilitators(newSelected);
  };

  const clearAllFilters = () => {
    setSelectedFacilitators(new Set());
  };

  const selectAllFacilitators = () => {
    setSelectedFacilitators(new Set(availableFacilitators.map(f => f.id || 'unassigned')));
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in current month (only relevant for month view)
  const isCurrentMonth = (date) => {
    if (view !== 'month') return true; // Always true for week/day views
    return date.getMonth() === calendarData.month;
  };

  // Format time from session start
  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Helper function to render facilitator badges
  const renderFacilitators = (session, maxVisible = 2) => {
    const facilitators = session.facilitators && session.facilitators.length > 0 
      ? session.facilitators 
      : [{ 
          id: session.facilitatorId, 
          name: session.facilitatorName || 'Unassigned', 
          color: session.facilitatorColor || 'gray' 
        }];

    const visibleFacilitators = facilitators.slice(0, maxVisible);
    const remainingCount = Math.max(0, facilitators.length - maxVisible);

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visibleFacilitators.map((facilitator, index) => {
          const colors = facilitatorColors[facilitator.color] || facilitatorColors.gray;
          return (
            <span 
              key={facilitator.id || index}
              className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}
              title={facilitator.name}
            >
              {facilitator.name}
            </span>
          );
        })}
        {remainingCount > 0 && (
          <span 
            className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200"
            title={`+${remainingCount} more: ${facilitators.slice(maxVisible).map(f => f.name).join(', ')}`}
          >
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Sessions Calendar</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className={`grid gap-2 ${
              view === 'month' ? 'grid-cols-5' : 
              view === 'week' ? 'grid-cols-5' : 
              'grid-cols-1'
            }`}>
              {Array.from({ 
                length: view === 'month' ? 25 : view === 'week' ? 5 : 1 
              }).map((_, i) => (
                <div key={i} className={`bg-gray-100 rounded animate-pulse ${
                  view === 'day' ? 'h-48' : 'h-24'
                }`}></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Sessions Calendar</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            {/* View Toggle */}
            <div className="flex items-center space-x-1 border rounded-md p-1">
              <Button
                variant={view === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('month')}
                className="text-xs px-2 py-1 h-6"
              >
                Month
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('week')}
                className="text-xs px-2 py-1 h-6"
              >
                Week
              </Button>
              <Button
                variant={view === 'day' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('day')}
                className="text-xs px-2 py-1 h-6"
              >
                Day
              </Button>
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={goToToday}
              className="text-xs"
            >
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={goToPrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[180px] text-center">
              {calendarData.displayName}
            </span>
            <Button variant="outline" size="sm" onClick={goToNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            
            {/* Filter Toggle */}
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="ml-2"
            >
              <Filter className="h-4 w-4 mr-1" />
              Filters
            </Button>
          </div>
        </div>
        
        {/* Facilitator Filters */}
        {showFilters && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Filter by Facilitator:</span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllFacilitators}
                  className="text-xs h-6"
                >
                  Select All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-xs h-6"
                >
                  Clear All
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableFacilitators.map((facilitator) => {
                const facilitatorKey = facilitator.id || 'unassigned';
                return (
                <Button
                  key={facilitatorKey}
                  variant={selectedFacilitators.has(facilitatorKey) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleFacilitatorFilter(facilitatorKey)}
                  className={`text-xs h-7 ${
                    selectedFacilitators.has(facilitatorKey) 
                      ? `${facilitator.bg} ${facilitator.border} ${facilitator.text} hover:opacity-80` 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full mr-2 ${facilitator.bg} ${facilitator.border} border`}></div>
                  {facilitator.name}
                </Button>
                );
              })}
            </div>
            {selectedFacilitators.size > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                Showing {filteredSessions.length} of {sessions.length} sessions
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading sessions for {view} view...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-red-500 mb-2">⚠️</div>
              <p className="text-sm text-red-600">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => {
                  const { startDate, endDate } = getViewDateRange();
                  fetchSessionsForView(startDate, endDate);
                }}
              >
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* Calendar content */}
        {!loading && !error && (
          <div className="space-y-4">
          {/* Day headers - only show for month and week views */}
          {view !== 'day' && (
            <div className={`grid gap-2 ${view === 'month' ? 'grid-cols-5' : 'grid-cols-5'}`}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
          )}
          
          {/* Calendar grid */}
          <div className={`grid gap-2 ${
            view === 'month' ? 'grid-cols-5' : 
            view === 'week' ? 'grid-cols-5' : 
            'grid-cols-1'
          }`}>
            {calendarData.days.map((date, index) => {
              const sessionsForDate = getSessionsForDate(date);
              const isCurrentMonthDay = isCurrentMonth(date);
              const isTodayDate = isToday(date);
              
              if (view === 'day') {
                // Day view - show sessions in a list format
                return (
                  <div key={index} className="space-y-4">
                    <div className={`p-4 border rounded-lg ${
                      isTodayDate ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                    }`}>
                      <h4 className={`text-lg font-semibold mb-4 ${
                        isTodayDate ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {date.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h4>
                      
                      {sessionsForDate.length === 0 ? (
                        <div className="text-center py-8">
                          <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">No sessions scheduled</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {sessionsForDate.map((session) => {
                            const facilitatorColor = getFacilitatorColor(session);
                            return (
                            <SessionTooltip key={session.id} session={session}>
                              <Link
                                to={getViewSOLink(session.soNumber, { location })}
                                className="block"
                              >
                                <div className={`p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer ${
                                  session.isPast 
                                    ? `${facilitatorColor.bg} ${facilitatorColor.border} opacity-60`
                                    : `${facilitatorColor.bg} ${facilitatorColor.border}`
                                }`}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <Clock className="h-4 w-4 flex-shrink-0" />
                                        <span className="font-semibold text-lg">
                                          {formatTime(session.rawStart)}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          session.isPast ? 'bg-gray-100 text-gray-600' :
                                          session.isToday ? 'bg-green-100 text-green-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`}>
                                          {session.isPast ? 'Completed' : session.isToday ? 'Today' : 'Upcoming'}
                                        </span>
                                        {renderFacilitators(session, 3)}
                                      </div>
                                      <h5 className="font-medium text-gray-900 mb-1">
                                        {session.sessionName}
                                      </h5>
                                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                                        <Users className="h-4 w-4 flex-shrink-0" />
                                        <span>SO: {session.soNumber}</span>
                                      </div>
                                    </div>
                                    <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  </div>
                                </div>
                              </Link>
                            </SessionTooltip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                // Month and week view - compact grid format
                return (
                  <div
                    key={index}
                    className={`min-h-[100px] p-2 border rounded-lg transition-colors ${
                      isTodayDate 
                        ? 'bg-blue-50 border-blue-200' 
                        : isCurrentMonthDay 
                        ? 'bg-white border-gray-200 hover:bg-gray-50' 
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    {/* Date number */}
                    <div className={`text-sm font-medium mb-1 ${
                      isTodayDate 
                        ? 'text-blue-600' 
                        : isCurrentMonthDay 
                        ? 'text-gray-900' 
                        : 'text-gray-400'
                    }`}>
                      {date.getDate()}
                    </div>
                    
                    {/* Sessions for this date */}
                    <div className="space-y-1">
                      {sessionsForDate.slice(0, 3).map((session) => {
                        const facilitatorColor = getFacilitatorColor(session);
                        return (
                        <SessionTooltip key={session.id} session={session}>
                          <Link
                            to={getViewSOLink(session.soNumber, { location })}
                            className="block"
                          >
                            <div className={`p-1 rounded text-xs hover:opacity-80 transition-opacity cursor-pointer border ${
                              session.isPast 
                                ? `${facilitatorColor.bg} ${facilitatorColor.border} ${facilitatorColor.text} opacity-60`
                                : `${facilitatorColor.bg} ${facilitatorColor.border} ${facilitatorColor.text}`
                            }`}>
                              <div className="flex items-center space-x-1 mb-1">
                                <Clock className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate font-medium">
                                  {formatTime(session.rawStart)}
                                </span>
                              </div>
                              <div className="truncate" title={session.sessionName}>
                                {session.sessionName}
                              </div>
                              <div className="mt-1 space-y-1 opacity-75">
                                <div className="flex items-center space-x-1">
                                  <Users className="h-3 w-3 flex-shrink-0" />
                                  <div className="truncate text-xs">
                                    {renderFacilitators(session, 1)}
                                  </div>
                                </div>
                                <div className="text-xs truncate" title={session.soNumber}>
                                  SO: {session.soNumber}
                                </div>
                              </div>
                            </div>
                          </Link>
                        </SessionTooltip>
                        );
                      })}
                      
                      {/* Show "+X more" if there are more than 3 sessions */}
                      {sessionsForDate.length > 3 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{sessionsForDate.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
          
          {/* Legend */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4 text-xs">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gray-100 border border-gray-200 rounded opacity-60"></div>
                  <span className="text-gray-600">Completed</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ExternalLink className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-600">Click sessions to view SO</span>
                </div>
              </div>
              {availableFacilitators.length > 0 && (
                <div className="text-xs text-gray-500">
                  Colors indicate facilitators
                </div>
              )}
            </div>
            
            {/* Facilitator Color Legend */}
            {availableFacilitators.length > 0 && !showFilters && (
              <div className="flex flex-wrap gap-3 text-xs">
                {availableFacilitators.slice(0, 6).map((facilitator) => (
                  <div key={facilitator.id || 'unassigned'} className="flex items-center space-x-1">
                    <div className={`w-3 h-3 rounded ${facilitator.bg} ${facilitator.border} border`}></div>
                    <span className="text-gray-600">{facilitator.name}</span>
                  </div>
                ))}
                {availableFacilitators.length > 6 && (
                  <div className="flex items-center space-x-1">
                    <span className="text-gray-500">+{availableFacilitators.length - 6} more</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionsCalendar;